import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, ButtonBase, Typography } from '@mui/material';
import { LOGIN_DEPARTMENTS } from '../constants/loginDepartments';
import FloatingFeedbackButton from './feedback/FloatingFeedbackButton';
import LoginBackgroundVideo, { FALLBACK_BACKGROUND } from './LoginBackgroundVideo';
import fleetwrightLogo from '../assets/fleetwright-logo-transparent.png';

/** Bold filled enterprise pictograms (viewBox 0 0 24 24) */
const PICTOGRAMS = {
  admin: (
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
  ),
  'accountable-manager': (
    <>
      <path d="M9 11c1.66 0 2.99-1.34 2.99-3S10.66 5 9 5 6 6.34 6 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h8.35A6.5 6.5 0 0110 16.5c0-.87.17-1.7.48-2.45C9.9 13.99 9.46 14 9 14z" />
      <path d="M18.01 13.33l.71-.55a.2.2 0 00.05-.26l-.67-1.16a.2.2 0 00-.24-.09l-.84.34c-.18-.13-.37-.23-.58-.3l-.13-.89a.2.2 0 00-.2-.16h-1.34a.2.2 0 00-.2.16l-.13.89c-.21.07-.4.17-.58.3l-.84-.34a.2.2 0 00-.24.09l-.67 1.16a.2.2 0 00.05.26l.71.55c-.02.12-.03.24-.03.36s.01.24.03.36l-.71.55a.2.2 0 00-.05.26l.67 1.16c.04.08.14.11.24.09l.84-.34c.18.13.37.23.58.3l.13.89c.01.09.09.16.2.16h1.34c.1 0 .18-.07.2-.16l.13-.89c.21-.07.4-.17.58-.3l.84.34c.1.03.2 0 .24-.09l.67-1.16a.2.2 0 00-.05-.26l-.71-.55c.02-.12.03-.24.03-.36s-.01-.24-.03-.36zM16.25 15.6a1.35 1.35 0 110-2.7 1.35 1.35 0 010 2.7z" />
    </>
  ),
  crew: (
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
  ),
  sales: (
    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
  ),
  ops: (
    <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.49.49 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.48.48 0 00-.48-.41h-3.84a.48.48 0 00-.48.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 00-.59.22L2.74 8.87a.48.48 0 00.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L2.86 14.52a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.48-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.49.49 0 00-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1112 8.4a3.6 3.6 0 010 7.2z" />
  ),
  camo: (
    <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" />
  ),
  store: (
    <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2S15.9 22 17 22s2-.9 2-2-.9-2-2-2zM7.2 14.8l.1-.2L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1 1 0 0019.96 4H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7.42c-.14 0-.25-.11-.22-.25z" />
  ),
  mro: (
    <>
      <path d="M12.5 4.5a3.5 3.5 0 00-3.16 5.01L4.5 14.35 6.65 16.5l4.84-4.84A3.5 3.5 0 1012.5 4.5zm0 5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
      <path d="M19.5 9.5a3.5 3.5 0 00-5.01 3.16L9.65 17.5 11.8 19.65l4.84-4.84A3.5 3.5 0 1019.5 9.5zm0 5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
    </>
  ),
  planning: (
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
  ),
  client: (
    <path d="M21 2H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h7v2H8v2h8v-2h-2v-2h7c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H3V4h18v12zM5 6h3v2H5V6zm0 3h3v2H5V9zm0 3h3v2H5v-2zm4-6h9v8H9V6z" />
  )
};

function ModulePictogram({ slug }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden
      sx={{
        width: '66%',
        height: '66%',
        display: 'block',
        fill: '#fff',
        color: '#fff'
      }}
    >
      {PICTOGRAMS[slug] || PICTOGRAMS.admin}
    </Box>
  );
}

const INK = '#1a2332';
const INK_MUTED = 'rgba(26, 35, 50, 0.72)';
const BADGE_COLOR = '#000000';

/** Static glow behind department badges */
const departmentGlowSx = (color = BADGE_COLOR) => ({
  position: 'relative',
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -10,
    borderRadius: '50%',
    background: `radial-gradient(circle, ${color}aa 0%, ${color}55 40%, transparent 72%)`,
    pointerEvents: 'none',
    zIndex: 0
  }
});

const hubDiscSx = {
  borderRadius: '50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  background:
    'radial-gradient(circle at 50% 42%, rgba(56, 152, 210, 0.42) 0%, rgba(20, 110, 175, 0.32) 45%, rgba(8, 70, 130, 0.22) 100%)',
  border: '3px solid rgba(40, 140, 200, 0.7)',
  boxShadow: `
    0 0 0 1cm rgba(6, 48, 95, 0.5),
    0 0 0 calc(1cm + 1px) rgba(10, 70, 125, 0.35),
    0 0 28px rgba(30, 130, 200, 0.45),
    0 0 56px rgba(10, 90, 160, 0.32),
    inset 0 0 36px rgba(40, 140, 200, 0.35)
  `
};

function DepartmentCard({
  dept,
  onClick,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  isActive,
  dimmed,
  compact = false
}) {
  return (
    <ButtonBase
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      focusRipple
      aria-label={`Sign in to ${dept.name}`}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 2 : { lg: 1.75, xl: 2.1 },
        px: compact ? 2.25 : { lg: 2, xl: 2.4 },
        py: compact ? 2.25 : { lg: 1.6, xl: 1.85 },
        minHeight: compact ? 108 : { lg: 112, xl: 124 },
        borderRadius: compact ? '18px' : '18px',
        textAlign: 'left',
        opacity: dimmed ? 0.32 : 1,
        transform: isActive ? 'translateY(-2px) scale(1.015)' : 'none',
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(245,247,250,0.92) 100%)',
        border: `1.5px solid ${dept.color}`,
        borderTop: `4px solid ${dept.color}`,
        boxShadow: isActive
          ? `0 18px 40px rgba(8,20,40,0.28), 0 0 22px ${dept.color}44`
          : `0 10px 26px rgba(8,20,40,0.16), 0 0 12px ${dept.color}28`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease',
        '&:hover, &:focus-visible': {
          boxShadow: `0 18px 40px rgba(8,20,40,0.28), 0 0 20px ${dept.color}40`
        }
      }}
    >
      <Box
        sx={{
          ...departmentGlowSx(BADGE_COLOR),
          width: compact ? 76 : { lg: 64, xl: 72 },
          height: compact ? 76 : { lg: 64, xl: 72 },
          borderRadius: '50%',
          backgroundColor: BADGE_COLOR,
          color: '#fff',
          border: '3.5px solid rgba(255,255,255,1)',
          outline: `2px solid ${BADGE_COLOR}`,
          outlineOffset: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: isActive
            ? `
              0 0 0 3px rgba(255,255,255,0.7),
              0 0 0 8px ${BADGE_COLOR}40,
              0 8px 20px rgba(8,20,40,0.28)
            `
            : `
              0 0 0 2px rgba(255,255,255,0.5),
              0 0 0 6px ${BADGE_COLOR}30,
              0 6px 16px rgba(8,20,40,0.22)
            `,
          '& > *': { position: 'relative', zIndex: 2 }
        }}
      >
        <ModulePictogram slug={dept.slug} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: INK,
            fontSize: compact ? '1.22rem' : { lg: '1.12rem', xl: '1.24rem' },
            lineHeight: 1.2
          }}
        >
          {dept.name}
        </Typography>
        <Typography
          sx={{
            mt: 0.4,
            fontSize: compact ? '0.92rem' : { lg: '0.84rem', xl: '0.92rem' },
            color: INK_MUTED,
            lineHeight: 1.4
          }}
        >
          {dept.description}
        </Typography>
        {dept.tagline ? (
          <Typography
            sx={{
              mt: 0.4,
              fontSize: compact ? '0.8rem' : { lg: '0.74rem', xl: '0.8rem' },
              color: dept.color,
              fontStyle: 'italic',
              fontWeight: 600
            }}
          >
            {dept.tagline}
          </Typography>
        ) : null}
      </Box>
    </ButtonBase>
  );
}

const LoginHub = () => {
  const navigate = useNavigate();
  const [hoveredSlug, setHoveredSlug] = useState(null);

  const { leftDepartments, rightDepartments } = useMemo(() => {
    const mid = Math.ceil(LOGIN_DEPARTMENTS.length / 2);
    return {
      leftDepartments: LOGIN_DEPARTMENTS.slice(0, mid),
      rightDepartments: LOGIN_DEPARTMENTS.slice(mid)
    };
  }, []);

  const openDepartment = (slug) => navigate(`/login/${slug}`);
  const setHover = (slug) => () => setHoveredSlug(slug);
  const clearHover = () => setHoveredSlug(null);
  const goTo = (slug) => () => openDepartment(slug);

  const renderColumn = (departments) =>
    departments.map((dept) => {
      const isActive = hoveredSlug === dept.slug;
      const dimmed = Boolean(hoveredSlug) && !isActive;
      return (
        <DepartmentCard
          key={dept.slug}
          dept={dept}
          onClick={goTo(dept.slug)}
          onFocus={setHover(dept.slug)}
          onBlur={clearHover}
          onMouseEnter={setHover(dept.slug)}
          onMouseLeave={clearHover}
          isActive={isActive}
          dimmed={dimmed}
        />
      );
    });

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100dvh',
        boxSizing: 'border-box',
        background: FALLBACK_BACKGROUND,
        overflow: { xs: 'auto', lg: 'hidden' }
      }}
    >
      <LoginBackgroundVideo fit="cover" />

      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse at 50% 48%, rgba(8,20,40,0.05) 0%, rgba(8,20,40,0.14) 75%, rgba(8,20,40,0.3) 100%)',
          zIndex: 0
        }}
      />

      {/* Desktop — 4 cards left, logo center, 4 cards right */}
      <Box
        component="nav"
        aria-label="Department login"
        sx={{
          display: { xs: 'none', lg: 'flex' },
          position: 'relative',
          zIndex: 1,
          width: '100%',
          height: '100dvh',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { lg: 3, xl: 4 },
          px: { lg: 3, xl: 4 },
          boxSizing: 'border-box'
        }}
      >
        <Box
          sx={{
            flex: '1 1 0',
            maxWidth: { lg: 380, xl: 420 },
            display: 'flex',
            flexDirection: 'column',
            gap: { lg: 1.5, xl: 1.75 },
            justifyContent: 'center',
            minWidth: 0
          }}
        >
          {renderColumn(leftDepartments)}
        </Box>

        <Box
          sx={{
            ...hubDiscSx,
            flexShrink: 0,
            width: { lg: 280, xl: 340 },
            aspectRatio: '1',
            px: 3
          }}
        >
          <Box
            component="img"
            src={fleetwrightLogo}
            alt="Fleetwright"
            sx={{
              width: '82%',
              maxWidth: 280,
              height: 'auto',
              display: 'block',
              mb: 1
            }}
          />
          <Typography
            sx={{
              fontSize: { lg: '0.82rem', xl: '0.92rem' },
              fontWeight: 500,
              letterSpacing: '0.02em',
              color: 'rgba(255,255,255,0.92)',
              textShadow: '0 1px 10px rgba(0,0,0,0.55)',
              lineHeight: 1.35,
              maxWidth: '88%'
            }}
          >
            Smart Aviation. Seamless Operations.
          </Typography>
        </Box>

        <Box
          sx={{
            flex: '1 1 0',
            maxWidth: { lg: 380, xl: 420 },
            display: 'flex',
            flexDirection: 'column',
            gap: { lg: 1.5, xl: 1.75 },
            justifyContent: 'center',
            minWidth: 0
          }}
        >
          {renderColumn(rightDepartments)}
        </Box>
      </Box>

      {/* Mobile / tablet list */}
      <Box
        component="nav"
        aria-label="Department login"
        sx={{
          display: { xs: 'flex', lg: 'none' },
          position: 'relative',
          zIndex: 1,
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          minHeight: '100dvh',
          px: 'max(16px, env(safe-area-inset-left))',
          pr: 'max(16px, env(safe-area-inset-right))',
          pt: 'max(20px, env(safe-area-inset-top))',
          pb: 'max(24px, env(safe-area-inset-bottom))',
          boxSizing: 'border-box',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <Box
          sx={{
            ...hubDiscSx,
            alignSelf: 'center',
            width: 'min(240px, 70%)',
            aspectRatio: '1',
            px: 2.5,
            mb: 1
          }}
        >
          <Box
            component="img"
            src={fleetwrightLogo}
            alt="Fleetwright"
            sx={{ width: '82%', height: 'auto', mb: 0.75 }}
          />
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: 'rgba(255,255,255,0.92)',
              textShadow: '0 1px 10px rgba(0,0,0,0.55)',
              textAlign: 'center',
              lineHeight: 1.35
            }}
          >
            Smart Aviation. Seamless Operations.
          </Typography>
        </Box>

        {LOGIN_DEPARTMENTS.map((dept) => (
          <DepartmentCard
            key={dept.slug}
            dept={dept}
            onClick={goTo(dept.slug)}
            compact
          />
        ))}
      </Box>

      <FloatingFeedbackButton />
    </Box>
  );
};

export default LoginHub;
